import React from 'react';
import { Button, Box } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
  updateFileStatus,
  updateFileProgress,
} from '../store/slices/uploadSlice';
import { uploadAPI, computeSHA256Hex } from '../services/uploadAPI';
import { EncryptionService } from '../services/encryptionService';

// SECURITY: encryptionPassword is NOT read from Redux state — it is passed as a
// prop from the parent component so that the passphrase never enters the global
// store (which is serialisable and inspectable via DevTools).
// (supportlogsio-1-196 L-1)
interface UploadProgressProps {
  encryptionPassword?: string | null;
}

const UploadProgress: React.FC<UploadProgressProps> = ({ encryptionPassword }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { files, sessionId, isEncryptionEnabled } = useSelector(
    (state: RootState) => state.upload
  );

  const pendingFiles = files.filter((f) => f.status === 'pending');
  const hasUploading = files.some((f) => f.status === 'uploading' || f.status === 'encrypting');

  const uploadFile = async (fileId: string) => {
    const fileInfo = files.find((f) => f.id === fileId);
    if (!fileInfo || !sessionId) return;

    try {
      // Get the actual file from input or stored reference
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      const actualFile = Array.from(fileInput?.files || []).find(
        (f) => f.name === fileInfo.name
      );

      if (!actualFile) {
        throw new Error('File not found');
      }

      let dataToUpload: ArrayBuffer;
      let fileName = actualFile.name;

      // Read plaintext bytes first so we can compute the SHA-256 before encryption.
      // The server cannot compute it because bytes go directly to S3 (supportlogsio-1-187).
      const plaintextBytes = await actualFile.arrayBuffer();
      const plaintextSHA256 = await computeSHA256Hex(plaintextBytes);

      // Encrypt if enabled
      if (isEncryptionEnabled && encryptionPassword) {
        dispatch(updateFileStatus({ id: fileId, status: 'encrypting' }));

        const encrypted = await EncryptionService.encryptFile(
          actualFile,
          encryptionPassword
        );

        dataToUpload = encrypted.encryptedData;
        fileName = `${fileName}.encrypted`;
      } else {
        dataToUpload = plaintextBytes;
      }

      dispatch(updateFileStatus({ id: fileId, status: 'uploading' }));

      // Get presigned URL
      const presignedResponse = await uploadAPI.getPresignedURL(
        sessionId,
        fileName,
        dataToUpload.byteLength
      );

      // Upload to S3
      await uploadAPI.uploadFile(
        presignedResponse.data.uploadUrl,
        dataToUpload,
        presignedResponse.data.headers,
        (progress) => {
          dispatch(updateFileProgress({ id: fileId, progress }));
        }
      );

      // Mark upload as complete, sending the plaintext SHA-256 for server-side persistence.
      await uploadAPI.completeUpload(
        sessionId,
        presignedResponse.data.fileId,
        plaintextSHA256 ?? undefined
      );

      dispatch(updateFileStatus({ id: fileId, status: 'completed' }));
    } catch (error: any) {
      dispatch(
        updateFileStatus({
          id: fileId,
          status: 'error',
          error: error.message || 'Upload failed',
        })
      );
    }
  };

  const startUpload = async () => {
    for (const file of pendingFiles) {
      await uploadFile(file.id);
    }
  };

  if (files.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
      <Button
        variant="contained"
        size="large"
        startIcon={<CloudUpload />}
        onClick={startUpload}
        disabled={pendingFiles.length === 0 || hasUploading}
      >
        {hasUploading
          ? 'Uploading...'
          : `Upload ${pendingFiles.length} File${pendingFiles.length !== 1 ? 's' : ''}`}
      </Button>
    </Box>
  );
};

export default UploadProgress;
